import { memo, type JSX } from 'react';
import { useInView } from 'react-intersection-observer';

import { ButtonEl } from 'components/site/site_sections';
import SiteAccordion from 'components/site/site-accordion';
import { createGlobalStyle } from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import { createMarkup } from 'lib/utils/create-markup';

const BenefitsStyles = createGlobalStyle`
  .pds08-benefits {
  max-width: none !important;
  padding: 0 !important;

  @media ${mqLargeMin} {
    margin-top: 50px;
  }

  .benefits-panel > .container {
    max-width: 1440px;
    padding: 30px;
    margin: 0 auto;

    @media ${mqLargeMin} {
      padding: 50px 50px 70px 50px;
    }

    > * {
      color: var(--text-primary);
    }
  }

  .benefits-title {
    font-size: 36px;
    font-weight: 300;
    color: var(--text-primary);
    line-height: 1.1;
    max-width: 1440px;
    margin: 0 auto;
    padding: 0px 30px;
    margin-bottom: 10px;

    @media ${mqLargeMin} {
      font-size: 50px;
      padding: 0px 50px;
      margin-bottom: 50px;
    }
  }

  .benefits-panel {
    width: 100%;

    > .container {
      position: relative;
      display: grid;
      grid-template-rows: auto auto auto;
      grid-template-columns: auto;
      grid-template-areas: 'top' 'aside' 'bottom';
      gap: 20px;

      @media ${mqLargeMin} {
        grid-template-rows: 1fr auto;
        grid-template-columns: 300px auto;
        grid-template-areas: 'top aside' 'bottom aside';
        gap: 0px 50px;
      }

      .top {
        grid-area: top;
      }

      .top-content {
        @media ${mqLargeMin} {
          position: sticky;
          top: 80px;
          height: fit-content;
        }
      }

      .bottom {
        grid-area: bottom;

        @media ${mqLargeMin} {
          margin-top: 50px;
        }
      }

      .aside {
        grid-area: aside;
      }
    }

    &:nth-of-type(1) {
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      padding-top: 30px;

      * {
        color: white !important;
        stroke: white;
      }

      .body * {
        background-color: transparent;
      }

      @media ${mqLargeMin} {
        padding-top: 50px;
      }
    }

    h3 {
      color: var(--text-primary);
      font-size: 26px;

      @media ${mqLargeMin} {
        font-size: 30px;
      }
    }

    p {
      color: var(--text-primary);
      font-size: 14px;
      margin: 1em 0;

      @media ${mqLargeMin} {
        font-size: 16px;
      }
    }

    h5 {
      color: var(--text-primary);
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 1em;
    }

    h6,
    .h6-style {
      color: var(--text-primary);
      font-size: 13px;
      line-height: 1.5;
    }

    .title {
      color: var(--text-primary);

      span {
        color: var(--text-primary);
        margin-left: 13.5px;
        align-self: center;
      }

      sup {
        color: var(--text-primary);
        font-size: 12px;
      }

      p {
        color: var(--text-primary);
        font-weight: 400;
        margin-top: 1em;
      }
    }

    .body {
      li,
      p {
        color: var(--text-primary);
        font-size: 16px;

        &:not(:last-of-type) {
          margin-bottom: 1em;
        }
      }
    }

    .mobile-accordion {
      .title {
        display: flex;

        img {
          min-width: 80px;
          height: 80px;
          border-radius: 50%;
          object-fit: cover;
        }
      }

      @media ${mqLargeMin} {
        display: none;
      }
    }

    .composition-desktop-panel,
    .strain-desktop-panel {
      display: none;

      @media ${mqLargeMin} {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 50px;
      }
    }
  }

  .composition-desktop-panel {
    .panel-item {
      opacity: 0;
      transform: translateY(100px);
      transition:
        transform 500ms ease-in-out,
        opacity 330ms ease-in-out;

      &:nth-of-type(2) {
        transition-delay: 100ms;
      }

      &:nth-of-type(3) {
        transition-delay: 200ms;
      }

      &:nth-of-type(4) {
        transition-delay: 300ms;
      }

      &:nth-of-type(5) {
        transition-delay: 400ms;
      }
    }

    .panel-image {
      height: 100px;
      margin-bottom: 25px;

      img,
      video {
        height: 100%;
      }
    }

    &.animate {
      .panel-item {
        transform: translateY(0%) !important;
        opacity: 1;
      }
    }
  }

  .strain-desktop-panel {
    .panel-item {
      &:nth-of-type(1) {
        z-index: 10;
      }

      &:nth-of-type(2) {
        z-index: 5;

        .panel-image {
          img,
          video {
            transform: translateX(-75%);
            transition-delay: 100ms;
          }
        }
      }

      &:nth-of-type(3) {
        .panel-image {
          img,
          video {
            transform: translateX(-150%);
            transition-delay: 200ms;
          }
        }
      }
    }

    .panel-image {
      position: relative;
      left: -25px;
      width: calc(100% + 50px);
      margin-bottom: 25px;
      aspect-ratio: 1/1;
      img,
      video {
        height: 100%;
        width: 100%;
        border: 1px solid var(--outline-neutral);
        object-fit: cover;
        border-radius: 50%;
        opacity: 0;
        transition:
          transform 500ms ease-in-out,
          opacity 330ms ease-in-out;
      }
    }

    &.animate {
      .panel-image {
        img,
        video {
          transform: translateX(0%) !important;
          opacity: 1;
        }
      }
    }
  }

  .panel-item {
    .panel-title {
      color: var(--text-primary);
      font-weight: 500;
    }

    .panel-content {
      li {
        color: var(--text-primary);
        border-bottom: 1px solid var(--outline-neutral);
        padding: 10px 0;

        &:first-of-type {
          border-top: 1px solid var(--outline-neutral);
        }
      }
    }
  }

  .padding-80 {
    padding: 50px;
  }
}

`;

type PanelItemProps = {
  title: string;
  image: string;
  isVideo: boolean;
  children: JSX.Element | JSX.Element[];
  classes: string[];
};

const PanelItem = memo(
  ({
    image,
    title,
    isVideo = false,
    children,
    classes = [],
  }: PanelItemProps) => {
    const className = classes.join(' ');
    return (
      <div className='panel-item'>
        <div className='panel-image'>
          {(isVideo && (
            <video playsInline webkit-playsInline autoPlay loop muted>
              <source src={image} type='video/mp4' />
            </video>
          )) || (
            <img src={image} alt={title} className={className} loading='lazy' />
          )}
        </div>
        <div
          className='panel-title'
          dangerouslySetInnerHTML={createMarkup(title)}
        />
        <div className='panel-content'>{children}</div>
      </div>
    );
  },
);
PanelItem.displayName = 'PanelItem';

const CompositionSubsection = memo(() => {
  const images = [
    'https://res.cloudinary.com/dljz0lko8/image/upload/q_auto/v1649821001/assets/pds08_benefits_bowel.svg',
    'https://res.cloudinary.com/dljz0lko8/image/upload/q_auto/v1649821001/assets/pds08_benefits_healthy.svg',
    'https://res.cloudinary.com/dljz0lko8/image/upload/q_auto/v1649821001/assets/pds08_benefits_tolerated.svg',
    'https://res.cloudinary.com/dljz0lko8/image/upload/q_auto/v1649821001/assets/pds08_benefits_bacteria.svg',
    'https://res.cloudinary.com/dljz0lko8/image/upload/q_auto/v1649821001/assets/pds08_benefits_fatty.svg',
  ];

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  return (
    <div
      className='benefits-panel'
      ref={ref}
      style={{
        backgroundImage: inView
          ? 'url(https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649820986/assets/Seed_textured_background_green.jpg)'
          : 'none',
      }}
    >
      <h2 className='benefits-title'>Evidence-based Benefits</h2>
      <div className='container' id='benefits'>
        <div className='top'>
          <div className='top-content'>
            <h3>Composition-specific Benefits</h3>
            {}
            <p>
              PDS-08® is the first 9-strain pediatric synbiotic to be studied
              in a double-blind, randomized 12-week trial in a pediatric
              population, demonstrating that it is tolerable and
              health-promoting for children and adolescents. The following
              benefits were observed in the composition as a whole:
            </p>

            <ButtonEl
              data={{
                fields: {
                  icon: 'plus_circle',
                  altIcon: 'minus_circle',
                  label: 'Benefits Citations',
                  modal: 'modal-pds08-benefits',
                },
              }}
            />
          </div>
        </div>
        <div
          className={`composition-desktop-panel aside ${
            inView ? 'animate' : ''
          }`}
        >
          <PanelItem
            image={images[0]}
            isVideo={false}
            title='Easy, frequent bowel movements.'
            classes={[]}
          >
            <p>
              Studied in a pediatric population <sup>1</sup> to support
              digestive function and regular, healthy bowel movements in
              children with occasional constipation.<sup>2</sup>
            </p>
          </PanelItem>

          <PanelItem
            image={images[1]}
            isVideo={false}
            title='Promotes healthy regularity.'
            classes={[]}
          >
            <p>
              Maintains healthy bowel movement regularity<sup>1</sup> among
              non-constipated<sup>2</sup> children without creating
              hyper-frequency of bowel movements.
            </p>
          </PanelItem>

          <PanelItem
            image={images[2]}
            isVideo={false}
            title='Exceptionally well tolerated.'
            classes={[]}
          >
            <p>
              No observed side effects or GI distress (such as pain, bloating,
              gas, diarrhea<sup>1</sup>) as commonly reported with many
              fiber-based dietary supplements, probiotics, and high-dose
              fermentable prebiotics.
            </p>
          </PanelItem>

          <PanelItem
            image={images[3]}
            isVideo={false}
            title='Increase of <i>Bifidobacterium</i>.'
            classes={[]}
          >
            <p>
              Significantly enriches <i>Bifidobacterium</i>
              <sup>3</sup> in the pediatric gut microbiome as demonstrated via
              metagenomic shotgun sequencing.<sup>4</sup>
            </p>
          </PanelItem>

          <PanelItem
            image={images[4]}
            isVideo={false}
            title='Production of Short-Chain Fatty Acids <i>in vitro</i>.'
            classes={[]}
          >
            <p>
              Significant increase in the concentration of SCFAs present in the
              colon compartment of SHIME®,<sup>5</sup> suggesting that PDS-08®
              supports the production of SCFAs in the lower gut.
            </p>
          </PanelItem>
        </div>
        <div className='mobile-accordion aside'>
          <SiteAccordion
            key='accordion-1'
            title={`<img src=${images[0]} loading="lazy" /><span>Easy, frequent bowel movements.</span>`}
          >
            <p>
              Studied in a pediatric population<sup>1</sup> to support digestive
              function and regular, healthy bowel movements in children with
              occasional constipation.<sup>2</sup>
            </p>
          </SiteAccordion>
          <SiteAccordion
            key='accordion-2'
            title={`<img src=${images[1]} loading="lazy" /><span>Promotes healthy regularity.</span>`}
          >
            <p>
              Maintains healthy bowel movement regularity<sup>1</sup> among
              non-constipated<sup>2</sup> children without creating
              hyper-frequency of bowel movements.
            </p>
          </SiteAccordion>
          <SiteAccordion
            key='accordion-3'
            title={`<img src=${images[2]} loading="lazy" /><span>Exceptionally well tolerated.</span>`}
          >
            <p>
              No observed side effects or GI distress (such as pain, bloating,
              gas, diarrhea<sup>1</sup>) as commonly reported with many
              fiber-based dietary supplements, probiotics, and high-dose
              fermentable prebiotics.
            </p>
          </SiteAccordion>
          <SiteAccordion
            key='accordion-4'
            title={`<img src=${images[3]} loading="lazy" /><span>Increase of <i>Bifidobacterium</i><sup>3</sup>.</span>`}
          >
            <p>
              Significantly enriches <i>Bifidobacterium</i>
              <sup>3</sup> in the pediatric gut microbiome as demonstrated via
              metagenomic shotgun sequencing.<sup>4</sup>
            </p>
          </SiteAccordion>
          <SiteAccordion
            key='accordion-5'
            title={`<img src=${images[4]} loading="lazy" /><span>Production of Short-Chain Fatty Acids <i>in vitro</i>.</span>`}
          >
            <p>
              Significant increase in the concentration of SCFAs present in the
              colon compartment of SHIME®,<sup>5</sup> suggesting that PDS-08®
              supports the production of SCFAs in the lower gut.
            </p>
          </SiteAccordion>
        </div>
      </div>
    </div>
  );
});
CompositionSubsection.displayName = 'PanelItem';

const StrainSubsection = memo(() => {
  const images = [
    'https://res.cloudinary.com/dljz0lko8/video/upload/f_auto,q_auto/v1622052953/daily-synbiotic/ezgif.com-gif-maker_akigra.mp4',
    'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649439154/PDS%20PDP/Skin.png',
    'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649439153/PDS%20PDP/Lungs.png',
  ];

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  return (
    <div className='benefits-panel' ref={ref}>
      <div className='container'>
        <div className='top'>
          <div className='top-content'>
            <h3>Strain-specific Benefits</h3>
            <p>
              In addition to conducting a trial on the PDS-08® formulation as a
              whole, all strains included have been studied in preclinical
              research and/or clinical trials in pediatric populations.
            </p>
          </div>
        </div>
        <div
          className={`strain-desktop-panel aside ${inView ? 'animate' : ''}`}
        >
          <PanelItem
            image={images[0]}
            isVideo
            title='Gastrointestinal Immunity/Health*'
            classes={['padding-80']}
          >
            <p>
              Formulated to support overall gastrointestinal function and
              healthy digestive responses.
            </p>
            <ul>
              <li>Supports gut health and comfort*</li>
              <li>Supports healthy regularity*</li>
              <li>Supports healthy stool consistency*</li>
              <li>Supports growth of healthy fecal bacteria*</li>
              <li>
                Supports gut integrity and barrier function during times of
                disruption*
              </li>
              <li>Supports intestinal immunity*</li>
            </ul>
          </PanelItem>

          <PanelItem
            image={images[1]}
            isVideo={false}
            title='Dermatological Health'
            classes={[]}
          >
            <ul>
              <li>Formulated with strains that support skin health*</li>
              <li>Formulated with strains that support the gut-skin axis*</li>
            </ul>
          </PanelItem>

          <PanelItem
            image={images[2]}
            isVideo={false}
            title='Respiratory Health'
            classes={[]}
          >
            <p>
              Formulated with strains that support immune health year round *
            </p>
            <ul>
              <li>
                Formulated with strains for respiratory health in a pediatric
                population*
              </li>
              <li>Promotes a healthy immune response*</li>
            </ul>
          </PanelItem>
        </div>
        <div className='mobile-accordion aside'>
          <SiteAccordion
            key='accordion-1'
            title={`
              <video playsInline height="80" width="80" autoplay style="object-fit:cover">
                <source src=${images[0]} />
              </video>
              <span>Gastrointestinal Immunity/Health</span>
            `}
          >
            <ul>
              <li>
                Formulated to support overall gastrointestinal function and
                healthy digestive responses.
              </li>
              <li>Supports gut health and comfort*</li>
              <li>Supports healthy regularity*</li>
              <li>Supports healthy stool consistency*</li>
              <li>Supports growth of healthy fecal bacteria*</li>
              <li>Supports intestinal immunity*</li>
            </ul>
          </SiteAccordion>
          <SiteAccordion
            key='accordion-2'
            title={`<img src=${images[1]} loading="lazy" /><span>Dermatological Health</span>`}
          >
            <ul>
              <li>Supports healthy, smooth skin*</li>
              <li>Supports clear skin*</li>
              <li>Reinforces the gut-skin axis*</li>
            </ul>
          </SiteAccordion>
          <SiteAccordion
            key='accordion-3'
            title={`<img src=${images[2]} loading="lazy" /><span>Respiratory Health</span>`}
          >
            <ul>
              <li>
                Validated in children to support healthy respiratory tract
                function and response to seasonal allergens.
              </li>
              <li>
                Supports healthy respiratory tract function and airway response*
              </li>
              <li>Supports a healthy response to seasonal allergens*</li>
            </ul>
          </SiteAccordion>
        </div>
        <div className='bottom'>
          <h6>
            *These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure or prevent any disease.
          </h6>
        </div>
      </div>
    </div>
  );
});
StrainSubsection.displayName = 'StrainSubsection';

const PDS08Benefits = memo(() => (
  <section className='pds08-benefits'>
    <BenefitsStyles />
    <CompositionSubsection />
    <StrainSubsection />
  </section>
));
PDS08Benefits.displayName = 'PDS08Benefits';

export default PDS08Benefits;
